// Tax Policy layer (phase 4) — CRUD for /tax-policies plus the per-document
// MANUAL tax-group writer (PATCH /{estimates|jobs|invoices}/:id/tax-group).
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { documentTaxGroupEndpoints, taxPolicyEndpoints } from '@/lib/api/endpoints';
import { taxPolicyKeys } from '@/lib/queryKeys';
import type { TaxPolicy, TaxPolicyInput } from '@/types/tax';

export function useTaxPolicies(options: { includeInactive?: boolean } = {}) {
  const suffix = options.includeInactive ? '?includeInactive=true' : '';
  return useApiQuery<TaxPolicy[]>({
    queryKey: [...taxPolicyKeys.list(), options.includeInactive ?? false],
    queryFn: () => apiGet<TaxPolicy[]>(operationsClient, `${taxPolicyEndpoints.list()}${suffix}`),
  });
}

export function useCreateTaxPolicy() {
  const qc = useQueryClient();
  return useApiMutation<TaxPolicy, TaxPolicyInput>({
    mutationFn: (body) => apiPost<TaxPolicy>(operationsClient, taxPolicyEndpoints.create(), body),
    onSuccess: (policy) => {
      toast.success(`Tax rule "${policy.name}" created.`);
      void qc.invalidateQueries({ queryKey: taxPolicyKeys.all() });
    },
  });
}

export function useUpdateTaxPolicy() {
  const qc = useQueryClient();
  return useApiMutation<TaxPolicy, { id: string } & TaxPolicyInput>({
    mutationFn: ({ id, ...body }) =>
      apiPatch<TaxPolicy>(operationsClient, taxPolicyEndpoints.update(id), body),
    onSuccess: (policy) => {
      toast.success(`Tax rule "${policy.name}" updated.`);
      void qc.invalidateQueries({ queryKey: taxPolicyKeys.all() });
    },
  });
}

export function useDeleteTaxPolicy() {
  const qc = useQueryClient();
  return useApiMutation<void, { id: string; name: string }>({
    mutationFn: ({ id }) => apiDelete<void>(operationsClient, taxPolicyEndpoints.remove(id)),
    onSuccess: (_data, vars) => {
      toast.success(`Tax rule "${vars.name}" deleted.`);
      void qc.invalidateQueries({ queryKey: taxPolicyKeys.all() });
    },
  });
}

export type TaxGroupOverrideDocument = 'estimate' | 'job' | 'invoice';

export interface SetDocumentTaxGroupPayload {
  document: TaxGroupOverrideDocument;
  documentId: string;
  /** Group to pin, or null to clear the pin. */
  taxGroupId: string | null;
  /** Required when pinning. */
  reason?: string;
}

/**
 * Pin a document to a tax group (taxResolvedFrom MANUAL) or clear the pin.
 * The caller invalidates its own document query on success — this hook only
 * knows tax caches.
 */
export function useSetDocumentTaxGroup(options: { onSuccess?: () => void } = {}) {
  return useApiMutation<unknown, SetDocumentTaxGroupPayload>({
    mutationFn: ({ document, documentId, taxGroupId, reason }) =>
      apiPatch<unknown>(operationsClient, documentTaxGroupEndpoints[document](documentId), {
        taxGroupId,
        ...(taxGroupId ? { reason } : {}),
      }),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.taxGroupId
          ? 'Tax group pinned — document repriced.'
          : 'Tax group pin cleared — document repriced.',
      );
      options.onSuccess?.();
    },
  });
}
